import { useContext } from 'react';
import { EnginesNote } from './engines';
import { TableFilterContext, filterWords, textMatches } from './tableFilter';
import type { BoardroomWhoRanks } from '../../../types';

/**
 * Who keeps coming up in this room's own searches.
 *
 * The table room 26 needed and never had: it asked who provides information on
 * baggy clothing, hunted brands for 25.8M tokens, and wrote `publishers: 0` —
 * while 312 searches sat in the database naming Reddit 105 times and Wirecutter,
 * GQ and Who What Wear eight to ten each.
 *
 * It costs nothing to draw: no seat fills it, no model reads it. It is what the
 * searches already said, counted.
 */

/** How many of the room's searches this domain turned up in, as a bar — the
 *  number that says a site owns a topic, rather than how many of its pages did. */
function Share({ n, of }: { n: number; of: number }) {
  const pct = of ? Math.round((n / of) * 100) : 0;
  return (
    <div className="flex items-center gap-1.5" title={`${n} of ${of} searches`}>
      <div className="h-1.5 w-16 rounded-full bg-gray-100 dark:bg-gray-700">
        <div
          className="h-1.5 rounded-full bg-indigo-400 dark:bg-indigo-500"
          style={{ width: `${Math.max(2, pct)}%` }}
        />
      </div>
      <span className="tabular-nums text-gray-600 dark:text-gray-300">{n}</span>
    </div>
  );
}

/** Whether a site has every word, in its name or the searches it came up for.
 *  The server counts the board's cards on the same two (`board_matches`). */
function whoRanksMatches(r: BoardroomWhoRanks['rows'][number], words: string[]): boolean {
  return textMatches(`${r.domain} ${r.queries.join(' ')}`, words);
}

export default function WhoRanksReport({
  data,
  limit,
}: {
  data: BoardroomWhoRanks;
  /** Card density shows a handful; the full view shows the table. */
  limit?: number;
}) {
  // THE ROOM'S FILTER. This table is drawn here rather than by `DataTable`,
  // so it reads the filter itself: a site matches on its name or the searches
  // it came up for.
  const filterText = useContext(TableFilterContext);
  const words = filterWords(filterText);
  const kept = words.length
    ? data.rows.filter(r => whoRanksMatches(r, words))
    : data.rows;
  const rows = limit ? kept.slice(0, limit) : kept;

  if (!data.searches) {
    return (
      <div className="text-xs text-gray-500 dark:text-gray-400">
        Nothing to count yet - this room has not run a search that landed.
      </div>
    );
  }
  if (words.length && !kept.length && data.rows.length) {
    return (
      <div className="text-xs text-gray-500 dark:text-gray-400" data-testid="filter-empty">
        None of the {data.shown} sites here match “{filterText.trim()}”.
      </div>
    );
  }
  if (!rows.length) {
    return (
      <div className="text-xs text-gray-500 dark:text-gray-400">
        {data.searches} searches, and no site came up in more than one of them. That is
        itself a finding: nobody owns this ground.
      </div>
    );
  }

  return (
    <div className="min-w-0">
      {!limit && (
        // THE DENOMINATORS. A domain in 3 of 4 searches and one in 3 of 300
        // read identically without them.
        <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
          {data.domains.toLocaleString()} sites across {data.results.toLocaleString()} results
          from {data.searches.toLocaleString()} of this room’s searches. Showing the{' '}
          {data.shown} that came up in more than one
          {words.length ? `; ${kept.length} of them match “${filterText.trim()}”` : ''}.
        </p>
      )}
      {!limit && <EnginesNote counts={data.engines} className="mb-2" />}
      <table className="w-full text-xs">
        <thead className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
          <tr>
            <th className="py-1 text-left font-normal">Site</th>
            <th className="py-1 text-left font-normal">Searches</th>
            <th className="py-1 text-right font-normal">Avg</th>
            {!limit && <th className="py-1 text-right font-normal">Best</th>}
            {!limit && <th className="py-1 text-left font-normal">Comes up for</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr
              key={r.domain}
              className="border-t border-gray-100 dark:border-gray-700/60 align-top"
            >
              <td className="py-1 pr-2">
                <a
                  href={`https://${r.domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="truncate text-gray-900 hover:underline dark:text-gray-100"
                >
                  {r.domain.replace(/^www\./, '')}
                </a>
              </td>
              <td className="py-1 pr-2">
                <Share n={r.searches} of={data.searches} />
              </td>
              <td className="py-1 pr-2 text-right tabular-nums text-gray-600 dark:text-gray-300">
                {r.avg_position ?? '-'}
              </td>
              {!limit && (
                <td className="py-1 pr-2 text-right tabular-nums text-gray-600 dark:text-gray-300">
                  {r.best_position ?? '-'}
                </td>
              )}
              {!limit && (
                <td className="py-1 text-gray-500 dark:text-gray-400">
                  <span className="line-clamp-2">{r.queries.join(' · ')}</span>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
